/**
 * OnboardingWizard.js
 * 3-step onboarding flow shown to first-time users.
 * Collects: goal, experience level, target role → personalizes the app.
 *
 * Props:
 *   onComplete(profile) — called when user finishes or skips
 *   theme — 'dark' | 'light'
 *   userName — optional user display name
 */

import React, { useState } from 'react';

// ─── CSS (injected once) ──────────────────────────────────────────────────────
const STYLE_ID = 'ob-wizard-styles';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes ob-fadeUp {
      from { opacity: 0; transform: translateY(18px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes ob-slideIn {
      from { opacity: 0; transform: translateX(24px); }
      to   { opacity: 1; transform: translateX(0); }
    }
    @keyframes ob-scaleIn {
      from { opacity: 0; transform: scale(0.95); }
      to   { opacity: 1; transform: scale(1); }
    }
    .ob-card { animation: ob-scaleIn 0.35s cubic-bezier(.4,0,.2,1) both; }
    .ob-step { animation: ob-fadeUp 0.3s cubic-bezier(.4,0,.2,1) both; }
    .ob-choice { transition: all 0.18s cubic-bezier(.4,0,.2,1); }
    .ob-choice:hover { transform: translateY(-2px); }
    .ob-btn { transition: all 0.18s ease; }
    .ob-btn:hover { opacity: 0.88; transform: scale(0.98); }
    .ob-btn:active { transform: scale(0.95); }
  `;
  document.head.appendChild(s);
}

// ─── Data ─────────────────────────────────────────────────────────────────────
const GOALS = [
  { id: 'get_hired',      emoji: '🎯', label: 'Get a New Job',        sub: 'Find and land my next role' },
  { id: 'change_career',  emoji: '🔄', label: 'Change Careers',       sub: 'Pivot to a new field or industry' },
  { id: 'internship',     emoji: '🎓', label: 'Internship / Campus',  sub: 'Freshers & college students' },
  { id: 'promotion',      emoji: '📈', label: 'Get a Promotion',      sub: 'Impress for an internal move' },
  { id: 'freelance',      emoji: '💻', label: 'Freelance / Contract', sub: 'Build a consulting profile' },
];

const EXPERIENCE = [
  { id: 'fresher',  emoji: '🌱', label: 'Fresher / Student',  sub: '0–1 year experience' },
  { id: '1_3yr',    emoji: '🚀', label: 'Early Career',       sub: '1–3 years experience' },
  { id: '3_7yr',    emoji: '⚡', label: 'Mid-Level',          sub: '3–7 years experience' },
  { id: '7yr_plus', emoji: '👑', label: 'Senior / Lead',      sub: '7+ years experience' },
];

const DOMAINS = [
  { id: 'software',    emoji: '💻', label: 'Software / Tech' },
  { id: 'product',     emoji: '📦', label: 'Product & Design' },
  { id: 'finance',     emoji: '📊', label: 'Finance & Banking' },
  { id: 'consulting',  emoji: '🏢', label: 'Consulting / Strategy' },
  { id: 'marketing',   emoji: '📣', label: 'Marketing & Growth' },
  { id: 'data',        emoji: '🔬', label: 'Data & Analytics' },
  { id: 'operations',  emoji: '⚙️',  label: 'Operations & Supply' },
  { id: 'hr',          emoji: '🤝', label: 'HR & People' },
  { id: 'sales',       emoji: '💰', label: 'Sales & BD' },
  { id: 'other',       emoji: '🌐', label: 'Other / General' },
];

// Template recommendation based on profile
function getRecommendedTemplate(goal, experience, domain) {
  if (experience === 'fresher') return 'campus_placement';
  if (experience === '1_3yr' && goal === 'internship') return 'internship_hunter';
  if (domain === 'finance') return 'finance_pro';
  if (domain === 'consulting') return 'product_consulting';
  if (domain === 'product') return 'product_consulting';
  if (domain === 'software' && (experience === '3_7yr' || experience === '7yr_plus')) return 'faang_engineering';
  if (experience === '7yr_plus') return 'executive_professional';
  if (experience === '3_7yr') return 'modern_ats_pro';
  return 'harvard_classic';
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function ChoiceGrid({ items, value, onChange, cols = 2, T }) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, 1fr)`,
      gap: 10,
    }}>
      {items.map(item => {
        const isSelected = value === item.id;
        return (
          <button
            key={item.id}
            className="ob-choice"
            onClick={() => onChange(item.id)}
            style={{
              padding: '14px 12px',
              borderRadius: 14,
              border: `1.5px solid ${isSelected ? T.accent : T.borderCard}`,
              background: isSelected ? `${T.accent}12` : T.cardBg,
              cursor: 'pointer',
              textAlign: 'left',
              boxShadow: isSelected
                ? `0 0 0 1px ${T.accent}30, 0 4px 16px ${T.accent}15`
                : T.cardShadow,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{item.emoji}</span>
              <div style={{ minWidth: 0 }}>
                <p style={{
                  fontSize: 12, fontWeight: 700, margin: '0 0 2px',
                  color: isSelected ? T.accent : T.textPrimary,
                  letterSpacing: '-0.2px',
                }}>{item.label}</p>
                {item.sub && (
                  <p style={{ fontSize: 10, color: T.textMuted, margin: 0, lineHeight: 1.4 }}>{item.sub}</p>
                )}
              </div>
              {isSelected && (
                <div style={{
                  marginLeft: 'auto', flexShrink: 0,
                  width: 18, height: 18, borderRadius: '50%',
                  background: T.accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                </div>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ProgressBar({ step, total, T }) {
  return (
    <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          flex: 1, height: 3, borderRadius: 3,
          background: i < step ? T.accent : T.borderCard,
          transition: 'background 0.3s ease',
          boxShadow: i < step ? `0 0 6px ${T.accent}60` : 'none',
        }}/>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function OnboardingWizard({ onComplete, theme = 'dark', userName }) {
  const [step, setStep] = useState(1);
  const [goal, setGoal] = useState('');
  const [experience, setExperience] = useState('');
  const [domain, setDomain] = useState('');

  const isDark = theme === 'dark';

  // Theme tokens
  const T = {
    overlay:    isDark ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0.4)',
    cardBg:     isDark ? '#121220' : '#ffffff',
    headerBg:   isDark ? '#0d0d1a' : '#f8fafc',
    borderCard: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    cardShadow: isDark ? '0 2px 8px rgba(0,0,0,0.4)' : '0 1px 4px rgba(0,0,0,0.06)',
    textPrimary:   isDark ? 'rgba(255,255,255,0.95)' : '#0f172a',
    textSecondary: isDark ? 'rgba(255,255,255,0.6)'  : '#475569',
    textMuted:     isDark ? 'rgba(255,255,255,0.35)'  : '#94a3b8',
    accent: '#6366f1',
    accentBg: isDark ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.08)',
    borderMain: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)',
    inputBg:  isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
    pillBg:   isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
  };

  const TOTAL_STEPS = 3;

  const handleNext = () => {
    if (step < TOTAL_STEPS) setStep(s => s + 1);
    else handleFinish();
  };

  const handleBack = () => {
    if (step > 1) setStep(s => s - 1);
  };

  const handleFinish = () => {
    const profile = {
      goal,
      experience,
      domain,
      recommendedTemplate: getRecommendedTemplate(goal, experience, domain),
      completedAt: new Date().toISOString(),
    };
    try {
      localStorage.setItem('mmr_onboarding', JSON.stringify(profile));
    } catch {
      // localStorage full or unavailable — non-critical, proceed anyway
      try { sessionStorage.setItem('mmr_onboarding', JSON.stringify(profile)); } catch {}
    }
    onComplete(profile);
  };

  const handleSkip = () => {
    const profile = { goal: '', experience: '', domain: '', skipped: true };
    try {
      localStorage.setItem('mmr_onboarding', JSON.stringify(profile));
    } catch {
      try { sessionStorage.setItem('mmr_onboarding', JSON.stringify(profile)); } catch {}
    }
    onComplete(profile);
  };

  const canProceed = [
    step === 1 ? !!goal : true,
    step === 2 ? !!experience : true,
    step === 3 ? !!domain : true,
  ].every(Boolean);

  // Step configurations
  const steps = [
    {
      label: 'What\'s your primary goal?',
      sub: 'We\'ll customize your experience to match your needs.',
      content: (
        <ChoiceGrid items={GOALS} value={goal} onChange={setGoal} cols={2} T={T}/>
      ),
    },
    {
      label: 'How much work experience do you have?',
      sub: 'This helps us recommend the best resume template for you.',
      content: (
        <ChoiceGrid items={EXPERIENCE} value={experience} onChange={setExperience} cols={2} T={T}/>
      ),
    },
    {
      label: 'What\'s your target field?',
      sub: 'We\'ll pre-fill relevant keywords and industry best practices.',
      content: (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
        }}>
          {DOMAINS.map(d => (
            <button
              key={d.id}
              className="ob-choice"
              onClick={() => setDomain(d.id)}
              style={{
                padding: '12px 14px',
                borderRadius: 12,
                border: `1.5px solid ${domain === d.id ? T.accent : T.borderCard}`,
                background: domain === d.id ? `${T.accent}12` : T.cardBg,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 10,
                boxShadow: domain === d.id
                  ? `0 0 0 1px ${T.accent}25, 0 4px 12px ${T.accent}12`
                  : T.cardShadow,
              }}
            >
              <span style={{ fontSize: 18 }}>{d.emoji}</span>
              <span style={{
                fontSize: 11.5, fontWeight: 700,
                color: domain === d.id ? T.accent : T.textPrimary,
                letterSpacing: '-0.2px',
              }}>{d.label}</span>
              {domain === d.id && (
                <div style={{
                  marginLeft: 'auto', flexShrink: 0,
                  width: 16, height: 16, borderRadius: '50%', background: T.accent,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>
      ),
    },
  ];

  const currentStep = steps[step - 1];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: T.overlay,
      backdropFilter: 'blur(12px)',
      padding: 20,
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
    }}>
      <div className="ob-card" style={{
        width: '100%', maxWidth: 540,
        background: T.cardBg,
        borderRadius: 24,
        border: `1px solid ${T.borderCard}`,
        boxShadow: isDark
          ? '0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.05)'
          : '0 32px 80px rgba(0,0,0,0.15)',
        overflow: 'hidden',
      }}>

        {/* ── Header ── */}
        <div style={{
          padding: '24px 28px 20px',
          background: isDark
            ? 'linear-gradient(135deg, rgba(99,102,241,0.12), rgba(139,92,246,0.08))'
            : 'linear-gradient(135deg, rgba(99,102,241,0.07), rgba(139,92,246,0.04))',
          borderBottom: `1px solid ${T.borderMain}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            {/* Logo / Brand */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img
                src="/resumebar-icon.svg"
                alt="Resumebar Icon"
                style={{ width: 36, height: 36, objectFit: 'contain' }}
              />
              <div>
                <p style={{ fontSize: 13, fontWeight: 800, color: T.textPrimary, margin: 0 }}>Resumebar</p>
                <p style={{ fontSize: 9, color: T.textMuted, margin: '1px 0 0' }}>AI Career Copilot</p>
              </div>
            </div>
            {/* Skip */}
            <button onClick={handleSkip} style={{
              fontSize: 11, fontWeight: 600, color: T.textMuted,
              background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
              borderRadius: 6,
            }}>
              Skip →
            </button>
          </div>

          {/* Welcome message */}
          <div>
            <h1 style={{ fontSize: 18, fontWeight: 800, color: T.textPrimary, margin: '0 0 4px', letterSpacing: '-0.4px' }}>
              Welcome{userName ? `, ${userName.split(' ')[0]}` : ''}! 👋
            </h1>
            <p style={{ fontSize: 11.5, color: T.textSecondary, margin: 0, lineHeight: 1.5 }}>
              Let's set up your AI career assistant in 60 seconds.
            </p>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ padding: '24px 28px' }}>
          {/* Progress bar */}
          <ProgressBar step={step} total={TOTAL_STEPS} T={T}/>

          {/* Step header */}
          <div className="ob-step" key={step} style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span style={{
                fontSize: 10, fontWeight: 800, color: T.accent,
                background: `${T.accent}18`, border: `1px solid ${T.accent}30`,
                padding: '2px 8px', borderRadius: 20, letterSpacing: '0.5px', textTransform: 'uppercase',
              }}>Step {step} of {TOTAL_STEPS}</span>
            </div>
            <h2 style={{ fontSize: 15, fontWeight: 800, color: T.textPrimary, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
              {currentStep.label}
            </h2>
            <p style={{ fontSize: 11, color: T.textMuted, margin: 0 }}>{currentStep.sub}</p>
          </div>

          {/* Step content */}
          <div className="ob-step" key={`content-${step}`} style={{ marginBottom: 24 }}>
            {currentStep.content}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {step > 1 && (
              <button className="ob-btn" onClick={handleBack} style={{
                padding: '11px 20px', borderRadius: 10, border: `1px solid ${T.borderCard}`,
                background: T.pillBg, color: T.textSecondary,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}>← Back</button>
            )}
            <button
              className="ob-btn"
              onClick={handleNext}
              disabled={!canProceed}
              style={{
                flex: 1, padding: '12px 20px', borderRadius: 10, border: 'none',
                background: canProceed
                  ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                  : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'),
                color: canProceed ? 'white' : T.textMuted,
                fontSize: 13, fontWeight: 700, cursor: canProceed ? 'pointer' : 'not-allowed',
                boxShadow: canProceed ? '0 4px 16px rgba(99,102,241,0.35)' : 'none',
                letterSpacing: '-0.2px',
              }}
            >
              {step === TOTAL_STEPS ? '🚀 Get Started' : `Continue →`}
            </button>
          </div>

          {/* Dots */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginTop: 16 }}>
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div key={i} style={{
                width: i + 1 === step ? 18 : 6,
                height: 6, borderRadius: 3,
                background: i + 1 === step ? T.accent : T.borderCard,
                transition: 'all 0.25s ease',
              }}/>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
